import React, { useContext, useEffect, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import {
  computeInstallmentDueDates,
  computeTotalValue,
  formatInstallmentPosition,
  getInstallmentDisplayLabel,
} from '../mappers';
import { formatCurrencyBRL } from '../financialFormatters';
import { FINANCIAL_ENTRY_RECURRENCE_OPTIONS, FINANCIAL_ENTRY_TYPE } from '../constants';
import financialEntryApi from '../api';
import { paymentMethodLabel, registerPaymentMethodCatalog } from '../../../../utils/paymentMethodLabels';
import {
  AddInstallmentButton,
  CardTitle,
  CommissionCell,
  DeleteIconButton,
  DeleteTrashIcon,
  FormGrid,
  InstallmentLabel,
  InstallmentRow,
  InstallmentsHeader,
  ParcelasPanel,
  ParcelasPanelHeader,
  ParcelasTitle,
  ReadOnlyValue,
  Slider,
  StyledCard,
  StyledFormGroup,
  SectionErrorList,
  SectionErrorMessage,
  SwitchInput,
  SwitchLabel,
  ToggleSwitchContainer,
  UpdateInstallmentsButton,
} from './style';
import FormOptionSelect from './FormOptionSelect';
import FormCurrencyField from './FormCurrencyField';
import FormPercentField from './FormPercentField';
import { FinancialEntryFormSchemaType } from './schema';
import { getInstallmentValidationMessages } from './formValidation';

interface PaymentSectionProps {
  isEdit?: boolean;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({ isEdit = false }) => {
  const { control, watch, setValue, formState: { errors } } = useFormContext<FinancialEntryFormSchemaType>();
  const { setFeedback } = useContext(FeedbackContext);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'installments',
  });

  const installments = watch('installments');
  const recurrence = watch('recurrence');
  const entryType = watch('type');
  const showCommission = entryType !== FINANCIAL_ENTRY_TYPE.EXPENSE;
  const installmentValidationMessages = getInstallmentValidationMessages(
    installments,
    errors.installments,
  );
  const total = computeTotalValue(
    (installments ?? []).map((inst) => ({
      ...inst,
      commission: inst.commission ?? '',
      installmentNumber: inst.installmentNumber ?? '',
    })),
  );

  useEffect(() => {
    financialEntryApi
      .getPaymentMethods()
      .then((methods) => {
        registerPaymentMethodCatalog(methods);
        setPaymentMethodOptions(
          methods.map((m) => ({ value: m.id, label: paymentMethodLabel(m.name) })),
        );
      })
      .catch(() => {
        setFeedback({
          message: 'Não foi possível carregar as formas de pagamento.',
          isOpen: true,
          statusOk: false,
        });
      });
  }, [setFeedback]);

  useEffect(() => {
    if (isEdit) return;

    const total = fields.length;
    for (let index = 0; index < total; index++) {
      setValue(
        `installments.${index}.installmentNumber`,
        formatInstallmentPosition(index, total),
        { shouldDirty: false },
      );
    }
  }, [fields.length, isEdit, setValue]);

  const handleAdd = () => {
    const nextTotal = fields.length + 1;
    append({
      value: '',
      dueDate: '',
      commission: '',
      hasCommission: false,
      installmentNumber: formatInstallmentPosition(nextTotal - 1, nextTotal),
    });
  };

  const canRemove = fields.length > 1 && !isEdit;

  const handleUpdateInstallmentDates = () => {
    const firstDueDate = installments?.[0]?.dueDate?.trim();

    if (!firstDueDate) {
      setFeedback({
        message: 'Informe a data da primeira parcela.',
        isOpen: true,
        statusOk: false,
      });
      return;
    }

    if (recurrence === null || recurrence === undefined) {
      setFeedback({
        message: 'Selecione a recorrência.',
        isOpen: true,
        statusOk: false,
      });
      return;
    }

    const dates = computeInstallmentDueDates(firstDueDate, recurrence, fields.length);
    if (!dates) {
      setFeedback({
        message: 'Não foi possível calcular as datas das parcelas.',
        isOpen: true,
        statusOk: false,
      });
      return;
    }

    dates.forEach((dueDate, index) => {
      setValue(`installments.${index}.dueDate`, dueDate, { shouldDirty: true });
    });
  };

  return (
    <StyledCard>
      <CardTitle>Pagamento</CardTitle>

      <FormGrid cols={3}>
        <div>
          <Controller
            name="paymentMethodId"
            control={control}
            render={({ field }) => (
              <FormOptionSelect
                label="Forma de pagamento"
                value={field.value ?? null}
                onChange={field.onChange}
                options={paymentMethodOptions}
                hasError={!!errors.paymentMethodId}
              />
            )}
          />
        </div>

        <StyledFormGroup>
          <label>Valor total</label>
          <ReadOnlyValue>{formatCurrencyBRL(total)}</ReadOnlyValue>
        </StyledFormGroup>

        <Controller
          name="recurrence"
          control={control}
          render={({ field }) => (
            <FormOptionSelect
              label="Recorrência"
              value={field.value ?? null}
              onChange={field.onChange}
              options={FINANCIAL_ENTRY_RECURRENCE_OPTIONS}
            />
          )}
        />
      </FormGrid>

      <ParcelasPanel>
        <ParcelasPanelHeader>
          <ParcelasTitle>Parcelas</ParcelasTitle>
          <UpdateInstallmentsButton
            type="button"
            onClick={handleUpdateInstallmentDates}
            disabled={fields.length < 2}
          >
            Atualizar parcelas
          </UpdateInstallmentsButton>
        </ParcelasPanelHeader>

        <InstallmentsHeader $hideCommission={!showCommission}>
          <span>Parcela</span>
          <span>Valor</span>
          <span>Data</span>
          {showCommission && <span>Comissão</span>}
          <span />
        </InstallmentsHeader>

        {fields.map((field, index) => {
          const hasCommission = watch(`installments.${index}.hasCommission`) ?? false;
          const installmentErrors = errors.installments?.[index];

          return (
            <InstallmentRow key={field.id} $hideCommission={!showCommission}>
              <Controller
                name={`installments.${index}.installmentNumber`}
                control={control}
                render={({ field: installmentNumberField }) => (
                  <>
                    <input type="hidden" {...installmentNumberField} value={installmentNumberField.value ?? ''} />
                    <InstallmentLabel>
                      {installmentNumberField.value ||
                        getInstallmentDisplayLabel(null, index, fields.length)}
                    </InstallmentLabel>
                  </>
                )}
              />

              <StyledFormGroup>
                <Controller
                  name={`installments.${index}.value`}
                  control={control}
                  render={({ field: f }) => (
                    <FormCurrencyField
                      id={`installment-value-${index}`}
                      name={f.name}
                      value={f.value ?? ''}
                      onChange={f.onChange}
                      hasError={!!installmentErrors?.value}
                    />
                  )}
                />
              </StyledFormGroup>

              <StyledFormGroup>
                <Controller
                  name={`installments.${index}.dueDate`}
                  control={control}
                  render={({ field: f }) => (
                    <input
                      {...f}
                      type="date"
                      aria-label={`Data da parcela ${index + 1}`}
                      data-field-error={installmentErrors?.dueDate ? 'true' : undefined}
                    />
                  )}
                />
              </StyledFormGroup>

              {showCommission && (
                <CommissionCell>
                  <ToggleSwitchContainer>
                    <SwitchLabel>
                      <Controller
                        name={`installments.${index}.hasCommission`}
                        control={control}
                        render={({ field: f }) => (
                          <SwitchInput
                            type="checkbox"
                            checked={!!f.value}
                            onChange={(e) => {
                              f.onChange(e.target.checked);
                              if (!e.target.checked) {
                                setValue(`installments.${index}.commission`, '');
                              }
                            }}
                          />
                        )}
                      />
                      <Slider className="slider" />
                    </SwitchLabel>
                  </ToggleSwitchContainer>
                  <StyledFormGroup style={{ flex: 1, margin: 0 }}>
                    <Controller
                      name={`installments.${index}.commission`}
                      control={control}
                      render={({ field: f }) => (
                        <FormPercentField
                          id={`installment-commission-${index}`}
                          name={f.name}
                          value={f.value ?? ''}
                          onChange={f.onChange}
                          disabled={!hasCommission}
                          placeholder="0,00%"
                          aria-label={`Comissão da parcela ${index + 1}`}
                        />
                      )}
                    />
                  </StyledFormGroup>
                </CommissionCell>
              )}

              <DeleteIconButton
                type="button"
                disabled={!canRemove}
                onClick={() => remove(index)}
                title="Remover parcela"
              >
                <DeleteTrashIcon />
              </DeleteIconButton>
            </InstallmentRow>
          );
        })}

        {!isEdit && (
          <AddInstallmentButton type="button" onClick={handleAdd}>
            <Plus size={16} />
            Adicionar parcela
          </AddInstallmentButton>
        )}

        {installmentValidationMessages.length > 0 && (
          <SectionErrorList>
            {installmentValidationMessages.map((message, messageIndex) => (
              <SectionErrorMessage key={`${message}-${messageIndex}`}>
                {message}
              </SectionErrorMessage>
            ))}
          </SectionErrorList>
        )}
      </ParcelasPanel>
    </StyledCard>
  );
};

export default PaymentSection;
